import type { ReservationState, ReservationPosItem, ReservationPosItemDto, ReservationDiffContainerDto, RoomBookingState } from './types';
import { countCalendarDaysBetween } from '@/utils/utils';
import { format, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { ReservationDiffToReservationDiff } from './model.Diffs';

export async function pollForChangeRequest(reservationId: string, changeRequestId: string) {
    // Poll for change request response is no longer in progress every 1 second

    const getChangeRequestInProgress = async () => {
        const response = await fetch(`/reservations/${reservationId}/change-requests/${changeRequestId}/in-progress`);
        const inProgress = await response.text();
        return inProgress === 'true';
    }

    while (await getChangeRequestInProgress()) {
        await new Promise(resolve => setTimeout(resolve, 1000));
    }
}

export function PosItemToPosItemState(pi: ReservationPosItemDto): ReservationPosItem {
    return {
        Description: pi.Description,
        Quantity: pi.Quantity,
        Amount: pi.Amount,
        ReservationOrderId: pi.ReservationOrderId,
        ReservationOrderLineItemId: pi.ReservationOrderLineItemId,
        RevenueDate: new Date(pi.RevenueDate),
        // Server-computed (lock-date boundary OR closed date) rather than a client-side date
        // comparison, so closed dates are reflected without shipping the closed-set to the browser.
        IsRevenueLocked: pi.IsRevenueLocked,
        CanRemove: pi.CanRemove,
        CanChangeAmount: pi.CanChangeAmount,
        IsPackage: pi.IsPackage
    };
}



export function countRoomNightsBetween(x: Date, y: Date): number {
    const daysBetween = countCalendarDaysBetween(x, y) - 1;

    if (daysBetween >= 0) {
        return daysBetween;
    }

    return 0;
}

export function countProgramDaysBetween(x: Date, y: Date): number {
    const daysBetween = countCalendarDaysBetween(x, y);
    if (daysBetween >= 0) {
        return daysBetween;
    }
    return 0;
}

export function getRevenueLockDateInputMin(revenueLockDate: Date | null) {
    if (!revenueLockDate) {
        return null;
    }

    return format(startOfDay(toZonedTime(revenueLockDate, 'Asia/Bangkok')), 'yyyy-MM-dd');
}

// --- Past-date quoting guard (blockPastDateQuoting) ---------------------------------------------
// "Today" is anchored to Bangkok local time regardless of the browser's own time zone, matching the
// server-side check on save. Client-side date pickers use these to grey out past dates; the server
// remains the source of truth (rejects with "past-date-blocked" if bypassed).

/** Start of "today" in Bangkok local time (BKK, UTC+7), independent of the browser's own time zone. */
export function bangkokStartOfToday(now = new Date()): Date {
    return startOfDay(toZonedTime(now, 'Asia/Bangkok'));
}

/** True when `date` falls on a calendar day before today, using Bangkok local time as the reference. */
export function isBeforeBangkokToday(date: Date, now = new Date()): boolean {
    return !!date && startOfDay(toZonedTime(date, 'Asia/Bangkok')).getTime() < bangkokStartOfToday(now).getTime();
}

// --- Open-Amount basis-change lifecycle ---------------------------------------------------------
// The canonical home for the basis-change override rules. Every basis-change dialog (Change Room
// Type, and - via S2 - Split / Shift / Add Breaks) reuses these so the drop semantics stay in one
// place. See @DOCSAPP/EN/room-row-pricing-architecture for the seam definition.

/**
 * Nulls the open-price override on a room row at both row and per-date scope. Mirrors exactly the
 * field set the Open-Amount sub-dialog's clear writes (OpenAmountComponent `clear()`): the row-level
 * `IgnoreVouchers` is a non-null boolean so it resets to `false`, while the per-date flag is nullable
 * so it resets to `null`. After this the server's fresh rate-plan compute stands on the next save.
 */
export function clearOpenPrice(row: RoomBookingState) {
    row.OpenPrice.setValue(null);
    row.OpenPriceDescription.setValue(null);
    row.IgnoreVouchers.setValue(false);

    row.Dates.forEach(d => {
        d.OpenPrice.setValue(null);
        d.OpenPriceDescription.setValue(null);
        d.IgnoreVouchers.setValue(null);
    });
}

/**
 * Retention predicate for the basis-change lifecycle: an open price is dropped on a basis change
 * unless the rate is locked. Also drives the pre-apply warning trigger.
 */
export function shouldDropOpenPrice(lockRate: boolean): boolean {
    return !lockRate;
}

/**
 * Canonical basis-change trigger: a room's room-type changing IS a basis change, so its open price
 * drops unless the rate is locked. Centralised here (and wired to every room's `RoomType` in the
 * reservation model) so EVERY surface that mutates a room's type - the inline row picker, the Change
 * Room Type dialog, auto-split - drops the override uniformly, with no per-call-site wiring. Returns
 * true when it actually cleared an override (for the caller / tests). No-op when the type didn't
 * really change, the rate is locked, or there's nothing to drop.
 */
export function dropOpenPriceOnRoomTypeChange(row: RoomBookingState, previousRoomType: string): boolean {
    if (row.RoomType.value === previousRoomType) {
        return false; // not a real basis change
    }
    if (!shouldDropOpenPrice(row.LockRate.value)) {
        return false; // rate locked → keep the open price
    }
    if (!hasOpenPrice(row)) {
        return false; // nothing to drop
    }
    clearOpenPrice(row);
    return true;
}

/** True when the row carries an active open-price override at row or per-date scope. */
export function hasOpenPrice(row: RoomBookingState): boolean {
    if (row.OpenPrice.value != null) {
        return true;
    }
    return row.Dates.some(d => d.OpenPrice.value != null);
}

/**
 * The open-price amount a single room row carries: the row-level override when set, otherwise the
 * sum of any per-date overrides (null when none). This is exactly the figure `clearOpenPrice` will
 * remove for that row, so the pre-apply warning reads the same number the operator loses.
 */
export function openPriceAmount(row: RoomBookingState): number | null {
    if (row.OpenPrice.value != null) {
        return row.OpenPrice.value;
    }
    const perDateTotal = row.Dates.reduce((sum, d) => sum + (d.OpenPrice.value ?? 0), 0);
    return perDateTotal || null;
}

/**
 * Total open price across the rows a basis-change action will actually clear (null when none carry
 * one). The date-changing dialogs pass only the changed segments here - not the whole reservation -
 * so the warning amount mirrors `clearOpenPrice`'s scope (carried over from the S1 review).
 */
export function totalOpenPriceAmount(rows: RoomBookingState[]): number | null {
    const total = rows.reduce((sum, r) => sum + (openPriceAmount(r) ?? 0), 0);
    return total || null;
}

export async function refreshDiffs(reservationState: ReservationState) {
    const response = await fetch(`/reservations/${reservationState.ReservationId}/diffs`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=utf-8'
        }
    });

    const responseData: GetDiffsResponse = await response.json();

    reservationState.OrderDiffs.setValue(responseData.diffs.map(d => ReservationDiffToReservationDiff(d)));
}

interface GetDiffsResponse {
    diffs: ReservationDiffContainerDto[];
}

export async function reloadPosItems(reservationState: ReservationState) {

    const response = await fetch(`/api/sales/reservations/${reservationState.ReservationId}/items`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=utf-8'
        }
    });

    const responseData: ReservationPosItemDto[] = await response.json();

    reservationState.PosItems.setValue(responseData.map(pi => PosItemToPosItemState(pi)));
}
